import { Link } from "react-router-dom";
import MarketingShell from "@/components/marketing/MarketingShell";
import { useProductName } from "@/context/BrandContext";
import {
  ArrowRight, Languages, Workflow, Mic2, Globe2, PhoneCall,
  Headphones, Keyboard, Bot, Users, TrendingDown, MapPin,
  CheckCircle2, Sparkles,
} from "lucide-react";

const FLAG_LANGS = [
  { code: "en-US", label: "English (US)", flag: "us", pct: 98 },
  { code: "en-GB", label: "English (UK)", flag: "gb", pct: 96 },
  { code: "es-ES", label: "Spanish", flag: "es", pct: 94 },
  { code: "fr-FR", label: "French", flag: "fr", pct: 93 },
  { code: "de-DE", label: "German", flag: "de", pct: 90 },
  { code: "tr-TR", label: "Turkish", flag: "tr", pct: 91 },
  { code: "zh-CN", label: "Chinese", flag: "cn", pct: 89 },
  { code: "ar-SA", label: "Arabic", flag: "sa", pct: 88 },
  { code: "hi-IN", label: "Hindi", flag: "in", pct: 92 },
  { code: "pt-BR", label: "Portuguese", flag: "br", pct: 91 },
];

const LANGS = FLAG_LANGS;

const HYBRID_STEPS = [
  { id: "rec", title: "Pre-recorded", sub: "Greeting · IVR · FAQ", cost: 1, tone: "hsl(255 70% 72%)" },
  { id: "dtmf", title: "DTMF / speech", sub: "Menu · capture digits", cost: 1.4, tone: "hsl(255 75% 62%)" },
  { id: "ai", title: "Live AI Agent", sub: "Qualify · sell · book", cost: 3.2, tone: "hsl(var(--primary))" },
  { id: "human", title: "Human only when needed", sub: "Warm SIP transfer", cost: 8.5, tone: "hsl(280 65% 48%)" },
];

function FlagImg({ code, alt, className = "" }) {
  return (
    <img
      src={`https://flagcdn.com/w80/${code}.png`}
      srcSet={`https://flagcdn.com/w40/${code}.png 1x, https://flagcdn.com/w80/${code}.png 2x`}
      alt={alt || ""}
      width={28}
      height={20}
      loading="lazy"
      className={`inline-block rounded-[3px] object-cover shadow-sm ${className}`}
    />
  );
}

function HeroGlobe() {
  // Chips stay upright; radius kept inside the box so longer names (Spanish/French) don't clip.
  const orbit = FLAG_LANGS.map((l, i) => {
    const angle = (i / FLAG_LANGS.length) * Math.PI * 2 - Math.PI / 2;
    const r = 38; // % from center — leaves room for pill width on the sides
    return {
      ...l,
      left: `${50 + r * Math.cos(angle)}%`,
      top: `${50 + r * Math.sin(angle)}%`,
      delay: `${0.05 + i * 0.07}s`,
    };
  });

  return (
    <div
      className="relative mx-auto aspect-square w-full max-w-[28rem] overflow-visible px-2 sm:px-3"
      data-testid="benefits-hero-visual"
      aria-hidden
    >
      <style>{`
        @keyframes ben-spin-ring { to { transform: rotate(360deg); } }
        @keyframes ben-pulse-ring {
          0% { transform: translate(-50%, -50%) scale(.92); opacity: .55; }
          70% { transform: translate(-50%, -50%) scale(1.22); opacity: 0; }
          100% { transform: translate(-50%, -50%) scale(1.22); opacity: 0; }
        }
        @keyframes ben-float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-7px)} }
        @keyframes ben-chip-in {
          from { opacity: 0; transform: translate(-50%, -50%) scale(.75); }
          to { opacity: 1; transform: translate(-50%, -50%) scale(1); }
        }
        @keyframes ben-core-pulse {
          0%,100% { transform: translate(-50%, -50%) scale(1); }
          50% { transform: translate(-50%, -50%) scale(1.045); }
        }
        @keyframes ben-chip-bob {
          0%,100% { transform: translate(-50%, -50%) translateY(0); }
          50% { transform: translate(-50%, -50%) translateY(-4px); }
        }
        .ben-float { animation: ben-float 5s ease-in-out infinite; }
        .ben-spin-ring { animation: ben-spin-ring 40s linear infinite; }
        .ben-pulse-ring {
          position: absolute; left: 50%; top: 50%;
          border-radius: 9999px;
          animation: ben-pulse-ring 2.8s ease-out infinite;
        }
        .ben-core {
          position: absolute; left: 50%; top: 50%;
          transform: translate(-50%, -50%);
          animation: ben-core-pulse 3.6s ease-in-out infinite;
        }
        .ben-lang-chip {
          position: absolute;
          transform: translate(-50%, -50%);
          max-width: min(9.5rem, 42%);
          animation: ben-chip-in .55s ease both, ben-chip-bob 3.4s ease-in-out infinite;
        }
      `}</style>

      <div className="ben-float absolute inset-3 sm:inset-4 overflow-visible">
        {/* Soft halo */}
        <div
          className="absolute inset-[8%] rounded-full"
          style={{
            background:
              "radial-gradient(circle at 40% 35%, hsl(var(--primary) / 0.26), transparent 62%), radial-gradient(circle at 70% 70%, hsl(280 70% 55% / 0.12), transparent 55%)",
          }}
        />

        {/* Pulse rings (centered via keyframes) */}
        <div className="ben-pulse-ring h-[68%] w-[68%] border-2 border-primary/35" />
        <div className="ben-pulse-ring h-[68%] w-[68%] border border-primary/20" style={{ animationDelay: "1s" }} />

        {/* Spinning dashed orbit track only (labels stay fixed on ring) */}
        <div className="ben-spin-ring absolute inset-[16%] rounded-full border border-dashed border-primary/35" />
        <div className="absolute inset-[26%] rounded-full border border-primary/15" />

        {/* Center core — translate kept inside animation so it stays centered */}
        <div className="ben-core z-10 grid h-[34%] w-[34%] place-items-center rounded-full bg-gradient-to-br from-[hsl(255_70%_42%)] via-[hsl(255_80%_28%)] to-[hsl(280_70%_22%)] text-center shadow-[0_0_40px_hsl(var(--primary)/0.45)] ring-4 ring-primary/25">
          <div className="px-2 sm:px-3">
            <p className="disp text-[13px] font-extrabold leading-tight text-white sm:text-base">30 Languages</p>
            <p className="mt-0.5 text-[10px] font-medium text-violet-100/90 sm:text-[11px]">local voices worldwide</p>
          </div>
        </div>

        {/* Flag chips — evenly placed, upright, kept inside the frame */}
        {orbit.map((l) => (
          <span
            key={l.code}
            className="ben-lang-chip z-20 inline-flex items-center gap-1 rounded-full border border-border/80 bg-background/95 px-2 py-1 text-[10px] font-semibold shadow-lg backdrop-blur-sm sm:gap-1.5 sm:px-2.5 sm:py-1.5 sm:text-[11px]"
            style={{ left: l.left, top: l.top, animationDelay: `${l.delay}, ${l.delay}` }}
          >
            <FlagImg code={l.flag} alt="" className="h-3 w-[1.125rem] shrink-0 sm:h-3.5 sm:w-5" />
            <span className="truncate">{l.label.split(" (")[0]}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function LanguageChart() {
  return (
    <div className="rounded-3xl border border-border bg-card/80 p-5 shadow-lg sm:p-7" data-testid="lang-chart">
      <div className="flex items-end justify-between gap-3">
        <div>
          <p className="disp text-lg font-bold">Language readiness</p>
          <p className="text-sm text-muted-foreground">Illustrative voice coverage by market locale</p>
        </div>
        <Globe2 className="h-5 w-5 text-primary" />
      </div>
      <ul className="mt-6 space-y-3">
        {LANGS.map((l, i) => (
          <li key={l.code} className="grid grid-cols-[9.5rem_1fr_2.5rem] items-center gap-3 sm:grid-cols-[11rem_1fr_3rem]">
            <span className="flex items-center gap-2 truncate text-sm font-medium">
              <FlagImg code={l.flag} alt="" className="h-3.5 w-5 shrink-0" />
              {l.label}
            </span>
            <div className="h-2.5 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-gradient-to-r from-primary to-[hsl(280_70%_62%)]"
                style={{
                  width: `${l.pct}%`,
                  animation: `ben-bar .9s ease both`,
                  animationDelay: `${i * 0.06}s`,
                  transformOrigin: "left",
                }}
              />
            </div>
            <span className="text-right font-mono text-xs text-muted-foreground">{l.pct}%</span>
          </li>
        ))}
      </ul>
      <style>{`
        @keyframes ben-bar { from { transform: scaleX(0); } to { transform: scaleX(1); } }
      `}</style>
    </div>
  );
}

function HybridCostChart() {
  const max = Math.max(...HYBRID_STEPS.map((s) => s.cost));
  return (
    <div className="rounded-3xl border border-border bg-card/80 p-5 shadow-lg sm:p-7" data-testid="hybrid-cost-chart">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="disp text-lg font-bold">Relative cost per minute</p>
          <p className="text-sm text-muted-foreground">Hybrid design keeps expensive human time for hot moments only</p>
        </div>
        <TrendingDown className="h-5 w-5 text-primary" />
      </div>
      <div className="mt-8 flex h-56 items-end justify-between gap-3 sm:gap-5">
        {HYBRID_STEPS.map((s, i) => {
          const h = Math.max(18, (s.cost / max) * 100);
          return (
            <div key={s.id} className="flex flex-1 flex-col items-center gap-2">
              <span className="font-mono text-[11px] font-semibold text-muted-foreground">{s.cost.toFixed(1)}×</span>
              <div className="relative flex h-40 w-full items-end justify-center">
                <div
                  className="ben-grow-bar w-[72%] max-w-[4.5rem] rounded-t-xl"
                  style={{
                    height: `${h}%`,
                    background: `linear-gradient(180deg, ${s.tone}, color-mix(in srgb, ${s.tone} 55%, transparent))`,
                    animationDelay: `${0.1 + i * 0.1}s`,
                    boxShadow: `0 0 24px color-mix(in srgb, ${s.tone} 35%, transparent)`,
                  }}
                />
              </div>
              <p className="disp text-center text-xs font-bold sm:text-sm">{s.title}</p>
              <p className="text-center text-[10px] text-muted-foreground sm:text-[11px]">{s.sub}</p>
            </div>
          );
        })}
      </div>
      <style>{`
        @keyframes ben-grow {
          from { transform: scaleY(0); opacity: .3; }
          to { transform: scaleY(1); opacity: 1; }
        }
        .ben-grow-bar {
          transform-origin: bottom center;
          animation: ben-grow .85s ease both;
        }
      `}</style>
      <p className="mt-5 text-center text-xs text-muted-foreground">
        Indexed view — your rates vary by provider; the shape of savings stays the same.
      </p>
    </div>
  );
}

function HybridFlowGraphic() {
  const nodes = [
    { Icon: Headphones, t: "Pre-recorded", d: "Open & educate" },
    { Icon: Keyboard, t: "DTMF / VAD", d: "Collect choice" },
    { Icon: Bot, t: "AI Live", d: "Sell & book" },
    { Icon: Users, t: "Human", d: "Only if needed" },
  ];
  return (
    <div className="rounded-3xl border border-border bg-card/80 p-5 shadow-lg sm:p-7" data-testid="hybrid-flow">
      <p className="disp text-lg font-bold">Hybrid call path</p>
      <p className="text-sm text-muted-foreground">Use cheap automation first — escalate only when value is proven</p>
      <div className="mt-8 grid gap-3 sm:grid-cols-4">
        {nodes.map((n, i) => (
          <div key={n.t} className="relative">
            <div
              className="rounded-2xl border border-border bg-background p-4 text-center transition-transform hover:-translate-y-1"
              style={{ animation: `m-rise .55s ease both`, animationDelay: `${i * 0.08}s` }}
            >
              <div className="mx-auto mb-3 grid h-11 w-11 place-items-center rounded-xl bg-primary/15 text-primary">
                <n.Icon className="h-5 w-5" />
              </div>
              <p className="disp text-sm font-bold">{n.t}</p>
              <p className="mt-1 text-[11px] text-muted-foreground">{n.d}</p>
            </div>
            {i < nodes.length - 1 && (
              <div className="absolute -right-2 top-1/2 z-10 hidden -translate-y-1/2 text-primary/70 sm:block" aria-hidden>
                →
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="mt-6 overflow-hidden rounded-2xl border border-dashed border-primary/35 bg-primary/[0.06] px-4 py-3 text-sm">
        <span className="font-semibold text-primary">Design tip: </span>
        <span className="text-muted-foreground">
          Keep identity, menus, and compliance lines on pre-recorded nodes. Reserve Live AI for objections, qualification, and booking — then warm-transfer only the winners.
        </span>
      </div>
    </div>
  );
}

function AccentCompareChart() {
  const rows = [
    { label: "Customer trust", old: 42, neu: 91 },
    { label: "First-call resolve", old: 38, neu: 84 },
    { label: "Appointment booked", old: 29, neu: 76 },
    { label: "Transfer success", old: 51, neu: 88 },
  ];
  return (
    <div className="rounded-3xl border border-border bg-card/80 p-5 shadow-lg sm:p-7" data-testid="accent-chart">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="disp text-lg font-bold">Offshore pain vs local-accent AI</p>
          <p className="text-sm text-muted-foreground">Illustrative outcome lift when the voice matches the market</p>
        </div>
        <div className="flex items-center gap-4 text-xs">
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-rose-400/80" /> Typical offshore accent gap</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-2.5 w-2.5 rounded-sm bg-primary" /> Local-accent AI agent</span>
        </div>
      </div>
      <div className="mt-8 space-y-5">
        {rows.map((r, i) => (
          <div key={r.label}>
            <div className="mb-1.5 flex justify-between text-sm">
              <span className="font-medium">{r.label}</span>
              <span className="font-mono text-xs text-muted-foreground">+{r.neu - r.old} pts</span>
            </div>
            <div className="space-y-1.5">
              <div className="h-2.5 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-rose-400/80"
                  style={{ width: `${r.old}%`, animation: `ben-bar .8s ease both`, animationDelay: `${i * 0.08}s` }}
                />
              </div>
              <div className="h-2.5 overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-primary to-[hsl(280_70%_68%)]"
                  style={{ width: `${r.neu}%`, animation: `ben-bar .9s ease both`, animationDelay: `${0.15 + i * 0.08}s` }}
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function AccentWave() {
  return (
    <div className="rounded-3xl border border-border bg-card/80 p-5 shadow-lg sm:p-7" aria-hidden data-testid="accent-wave">
      <p className="disp text-lg font-bold">Sounds like home</p>
      <p className="text-sm text-muted-foreground">Live AI speaks the locale — sales, booking, then warm transfer</p>
      <div className="mt-6 flex items-end justify-center gap-1.5 h-24">
        {[12, 28, 18, 36, 22, 40, 16, 32, 24, 38, 14, 30, 20, 34, 18, 26].map((h, i) => (
          <span
            key={i}
            className="w-2 rounded-full bg-gradient-to-t from-primary to-[hsl(280_70%_68%)] sm:w-2.5"
            style={{
              height: h * 2,
              animation: `ben-wave 1.05s ease-in-out infinite`,
              animationDelay: `${i * 0.07}s`,
            }}
          />
        ))}
      </div>
      <style>{`
        @keyframes ben-wave { 0%,100%{transform:scaleY(.35)} 50%{transform:scaleY(1)} }
      `}</style>
      <ul className="mt-6 grid gap-2 text-sm sm:grid-cols-3">
        {["Complete sales pitch", "Book appointments", "Transfer to local agent"].map((t) => (
          <li key={t} className="flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2.5">
            <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
            <span>{t}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

const SECTIONS = [
  {
    id: "languages",
    label: "Languages",
    Icon: Languages,
    n: "01",
    title: "30 languages, one canvas",
    blurb: "Run US, UK, EU, MENA & APAC voices from the same flow designer.",
    tone: "from-violet-500/20 to-primary/5",
  },
  {
    id: "hybrid",
    label: "Hybrid cost",
    Icon: Workflow,
    n: "02",
    title: "Spend AI only where it pays",
    blurb: "Pre-recorded + DTMF first — Live AI and humans only for hot moments.",
    tone: "from-fuchsia-500/15 to-primary/5",
  },
  {
    id: "accent",
    label: "Local accent",
    Icon: Mic2,
    n: "03",
    title: "Sound next door, not offshore",
    blurb: "Full sales, booking, and warm transfer in a market-native accent.",
    tone: "from-indigo-500/20 to-primary/5",
  },
];

export default function Benefits() {
  const productName = useProductName();
  const name = productName || "our platform";

  return (
    <MarketingShell active="benefits">
      {/* Hero — brand first, one composition */}
      <section className="relative overflow-hidden border-b border-border">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(1000px 480px at 10% -15%, hsl(var(--primary) / 0.16), transparent 55%), radial-gradient(800px 420px at 95% 0%, hsl(var(--primary) / 0.14), transparent 50%), linear-gradient(180deg, hsl(var(--background)), hsl(var(--muted) / 0.35))",
          }}
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage: "radial-gradient(hsl(var(--border)) 1px, transparent 1px)",
            backgroundSize: "22px 22px",
          }}
        />
        <div className="relative mx-auto max-w-7xl px-6 pb-14 pt-14 sm:pt-16 lg:pb-20 lg:pt-20">
          <div className="grid items-center gap-10 lg:grid-cols-[1.05fr_0.95fr] lg:gap-14">
            <div className="m-rise max-w-2xl">
              <p className="disp text-sm font-semibold tracking-wide text-primary">
                {productName || "AI Voice"}
              </p>
              <h1 className="disp mt-3 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-[3.25rem] lg:leading-[1.08]">
                Benefits that change how call centers scale
              </h1>
              <p className="mt-5 max-w-xl text-lg text-muted-foreground">
                Speak every market, spend AI minutes only where they matter, and sound local —
                so offshore teams stop losing deals to accent and language friction.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <a
                  href="#languages"
                  className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
                >
                  Explore benefits <ArrowRight className="h-4 w-4" />
                </a>
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-2 rounded-xl border border-border px-6 py-3.5 text-sm font-medium hover:bg-muted"
                >
                  Get started
                </Link>
              </div>
            </div>
            <div className="m-rise" style={{ animationDelay: "0.12s" }}>
              <HeroGlobe />
            </div>
          </div>

          <nav className="mt-14" aria-label="Benefits sections">
            <div className="mb-5 flex items-end justify-between gap-4">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-primary">Core benefits</p>
                <p className="disp mt-1 text-xl font-extrabold tracking-tight sm:text-2xl">
                  Three reasons teams switch
                </p>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-3">
              {SECTIONS.map((s, i) => (
                <a
                  key={s.id}
                  href={`#${s.id}`}
                  data-testid={`benefits-jump-${s.id}`}
                  className="group relative overflow-hidden rounded-2xl border border-border bg-card p-5 shadow-md transition-all duration-300 hover:-translate-y-1.5 hover:border-primary/40 hover:shadow-xl"
                  style={{ animation: `m-rise .55s ease both`, animationDelay: `${0.08 + i * 0.08}s` }}
                >
                  <div
                    className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${s.tone} opacity-80 transition-opacity group-hover:opacity-100`}
                  />
                  <div className="relative">
                    <div className="flex items-start justify-between gap-3">
                      <div className="grid h-12 w-12 place-items-center rounded-2xl bg-primary text-primary-foreground shadow-lg shadow-primary/30 transition-transform group-hover:scale-110">
                        <s.Icon className="h-6 w-6" />
                      </div>
                      <span className="font-mono text-xs font-semibold text-primary/80">{s.n}</span>
                    </div>
                    <p className="disp mt-4 text-lg font-extrabold tracking-tight">{s.label}</p>
                    <p className="mt-1 text-sm font-semibold text-foreground/90">{s.title}</p>
                    <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.blurb}</p>
                    <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary">
                      Explore <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </span>
                  </div>
                </a>
              ))}
            </div>
          </nav>
        </div>
      </section>

      {/* 1. Multi language */}
      <section id="languages" className="scroll-mt-24 border-b border-border">
        <div className="mx-auto max-w-7xl px-6 py-16 sm:py-20">
          <div className="max-w-2xl m-rise">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-primary">01 · Multi language</p>
            <h2 className="disp mt-3 text-3xl font-extrabold tracking-tight sm:text-4xl">
              One platform, many languages
            </h2>
            <p className="mt-4 text-muted-foreground">
              {name} lets each AI Agent node speak the language of the campaign — so the same flow designer
              can run English, Spanish, French, German, Turkish, Chinese, Arabic, Hindi, Portuguese, and more without rebuilding your stack.
            </p>
          </div>

          <div className="mt-10 grid gap-8 lg:grid-cols-2 lg:items-start">
            <LanguageChart />
            <div className="space-y-4 m-rise" style={{ animationDelay: "0.1s" }}>
              {[
                { Icon: Languages, t: "Per-node language & voice", d: "Pick locale and voice on every AI Agent — mix markets inside one account." },
                { Icon: Globe2, t: "Same flow, local script", d: "Keep your visual flow; swap language, call script, and knowledge for each region." },
                { Icon: Sparkles, t: "Natural turn-taking", d: "Live duplex listens and replies in-language with barge-in that feels human." },
              ].map((item) => (
                <div key={item.t} className="flex gap-4 rounded-2xl border border-border bg-background/60 p-5">
                  <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/15 text-primary">
                    <item.Icon className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="disp font-bold">{item.t}</p>
                    <p className="mt-1 text-sm text-muted-foreground">{item.d}</p>
                  </div>
                </div>
              ))}
              <div className="rounded-2xl border border-primary/30 bg-primary/[0.07] p-5">
                <p className="disp text-sm font-bold text-foreground">Why it matters</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Expanding to a new country used to mean a new dialer script vendor. Now it is a language + voice + script change on the same campaign canvas.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2. Hybrid cost model */}
      <section id="hybrid" className="scroll-mt-24 border-b border-border bg-muted/25">
        <div className="mx-auto max-w-7xl px-6 py-16 sm:py-20">
          <div className="max-w-2xl m-rise">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-primary">02 · Hybrid cost model</p>
            <h2 className="disp mt-3 text-3xl font-extrabold tracking-tight sm:text-4xl">
              Design the call to spend less
            </h2>
            <p className="mt-4 text-muted-foreground">
              Not every second needs a Live AI brain or a human agent. Use pre-recorded audio and DTMF where
              the interaction is structured — then bring Live AI (and humans) only when conversation creates value.
            </p>
          </div>

          <div className="mt-10 space-y-8">
            <HybridFlowGraphic />
            <div className="grid gap-8 lg:grid-cols-2">
              <HybridCostChart />
              <div className="space-y-4 m-rise" style={{ animationDelay: "0.08s" }}>
                <div className="rounded-3xl border border-border bg-card/80 p-6 shadow-lg">
                  <p className="disp text-lg font-bold">Where pre-recorded wins</p>
                  <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
                    {[
                      "Legal / compliance openers that must be word-perfect",
                      "Brand intros and product one-liners you already approved",
                      "IVR menus: press 1 for sales, 2 for support",
                      "After-hours messages and simple status updates",
                    ].map((t) => (
                      <li key={t} className="flex gap-2">
                        <Headphones className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-3xl border border-border bg-card/80 p-6 shadow-lg">
                  <p className="disp text-lg font-bold">Where Live AI + transfer wins</p>
                  <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
                    {[
                      "Objections, discovery, and consultative selling",
                      "Appointment booking with confirmation",
                      "Warm transfer when the prospect is ready for a human",
                    ].map((t) => (
                      <li key={t} className="flex gap-2">
                        <Bot className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        <span>{t}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. Local accent / offshore */}
      <section id="accent" className="scroll-mt-24 border-b border-border">
        <div className="mx-auto max-w-7xl px-6 py-16 sm:py-20">
          <div className="max-w-2xl m-rise">
            <p className="font-mono text-xs uppercase tracking-[0.22em] text-primary">03 · Local accent AI</p>
            <h2 className="disp mt-3 text-3xl font-extrabold tracking-tight sm:text-4xl">
              Fix the #1 offshore call-center problem
            </h2>
            <p className="mt-4 text-muted-foreground">
              Accent mismatch kills trust in the first ten seconds. {name} runs complete sales,
              appointment booking, and warm transfers in a local accent — so your offshore operation
              sounds like it sits next door to the customer.
            </p>
          </div>

          <div className="mt-10 grid gap-8 lg:grid-cols-2">
            <AccentCompareChart />
            <AccentWave />
          </div>

          <div className="mt-10 grid gap-4 sm:grid-cols-3">
            {[
              { Icon: MapPin, t: "Market-native voice", d: "Choose voices that match General American, UK, South Asia, MENA, LATAM, and more." },
              { Icon: PhoneCall, t: "Full sales cycle", d: "Open, qualify, handle objections, book the calendar, then bridge a human if needed." },
              { Icon: Users, t: "Offshore without the friction", d: "Keep cost leverage offshore while the customer hears a local, confident agent." },
            ].map((c, i) => (
              <div
                key={c.t}
                className="rounded-2xl border border-border bg-background/70 p-5 m-rise"
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                <div className="grid h-10 w-10 place-items-center rounded-xl bg-primary/15 text-primary">
                  <c.Icon className="h-5 w-5" />
                </div>
                <p className="disp mt-4 font-bold">{c.t}</p>
                <p className="mt-2 text-sm text-muted-foreground">{c.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative overflow-hidden">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(700px 280px at 50% 0%, hsl(var(--primary) / 0.14), transparent 70%)",
          }}
        />
        <div className="relative mx-auto max-w-3xl px-6 py-16 text-center sm:py-20">
          <h2 className="disp text-3xl font-extrabold tracking-tight sm:text-4xl">
            Put these benefits on your next campaign
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
            Design hybrid flows, pick the language and voice, and launch Live AI that sounds local —
            then transfer only the conversations that deserve a human.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/signup"
              className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3.5 text-sm font-semibold text-primary-foreground transition-transform hover:-translate-y-0.5"
            >
              Get started <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/features"
              className="inline-flex items-center gap-2 rounded-xl border border-border px-6 py-3.5 text-sm font-medium hover:bg-muted"
            >
              See all features
            </Link>
          </div>
        </div>
      </section>
    </MarketingShell>
  );
}
